import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  EmptyRow, ErpTable, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { ErrorStrip, Forbidden, LoadingBlock } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import {
  expenseCategories, listEvents, listExpenses, paymentMethods, recordExpense,
  reversalReasons, reverseExpense, type Expense,
} from './api'

/**
 * The ledger — every entry, newest first.
 *
 * ## Corrections are lines, not edits
 *
 * There is no edit button and no delete button, and that is the design rather than an
 * omission. A correction is a REVERSAL: a new negative line naming what it corrects and
 * why. The original stays, struck through, and the net total moves.
 *
 * A reader seeing two lines where they expected one is seeing the feature work. A ledger
 * somebody can edit answers "what does it say now" and cannot answer "what changed", which
 * is the only question an audit asks.
 */
export function LedgerPage() {
  const canRead = useCan('finance:read')
  const canRecord = useCan('finance:record')
  const canReverse = useCan('finance:reverse')

  const [recording, setRecording] = useState(false)
  const [reversing, setReversing] = useState<Expense | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [category, setCategory] = useState('')
  /*
   * `?from=&to=` narrows the ledger to a window. The calendar's "KES 312,000 spent" has no
   * single row behind it, so it links here for that DAY — which is only useful if the page
   * honours the dates rather than showing every entry ever recorded.
   */
  const [params, setParams] = useSearchParams()
  const from = params.get('from') ?? undefined
  const to = params.get('to') ?? undefined

  const rows = useLoad(
    () => listExpenses({ size: 200, category: category || undefined, from, to }),
    [category, from, to])
  const categories = useLoad(expenseCategories, [])

  if (!canRead) return <Forbidden what="The campaign ledger" />

  const entries = rows.data?.content ?? []

  return (
    <div className="space-y-4">
      <SectionHeader
        title="The ledger"
        description="Every entry, newest first. A correction appears as its own line — nothing
                     here is edited or deleted, because a return built on rows somebody can
                     change is not evidence of anything."
        right={(
          <div className="flex items-center gap-2">
            <select value={category} onChange={(e) => setCategory(e.target.value)}
                    className="h-9 rounded border border-border bg-background px-2 text-erp-sm">
              <option value="">Every heading</option>
              {(categories.data ?? []).map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
            {canRecord && (
              <button type="button" className="erp-btn-primary h-9 text-erp-sm"
                      onClick={() => setRecording(true)}>
                Record spending
              </button>
            )}
          </div>
        )}
      />

      <ErrorStrip message={error ?? rows.error} />

      {from || to ? (
        <div className="flex items-center gap-2 text-erp-sm">
          <span className="rounded border border-border bg-muted/40 px-2 py-1 text-erp-xs">
            {from === to ? from : `${from ?? 'the start'} to ${to ?? 'today'}`}
          </span>
          <button type="button"
                  onClick={() => {
                    const next = new URLSearchParams(params)
                    next.delete('from')
                    next.delete('to')
                    setParams(next, { replace: true })
                  }}
                  className="text-erp-xs underline underline-offset-2 text-muted-foreground">
            Show every entry
          </button>
        </div>
      ) : null}

      {rows.loading && entries.length === 0 ? (
        <LoadingBlock label="Loading the ledger…" />
      ) : (
        <Panel>
          <ErpTable head={(
            <>
              <Th>Reference</Th>
              <Th>Date</Th>
              <Th>Heading</Th>
              <Th>Who it went to</Th>
              <Th>Where</Th>
              <Th className="text-right">Amount</Th>
              <Th>Paid by</Th>
              <Th>Receipt</Th>
              <Th />
            </>
          )}>
            {entries.length === 0 ? (
              <EmptyRow colSpan={9}
                message="Nothing recorded yet. Record the first entry — a fuel receipt, a
                         printing invoice, a hall hire." />
            ) : entries.map((row) => (
              <Row key={row.id} row={row} canReverse={canReverse}
                   onReverse={() => setReversing(row)} />
            ))}
          </ErpTable>
        </Panel>
      )}

      {recording && (
        <RecordDialog
          onClose={() => setRecording(false)}
          onDone={() => { setRecording(false); void rows.reload() }}
          onError={setError}
        />
      )}
      {reversing && (
        <ReverseDialog
          expense={reversing}
          onClose={() => setReversing(null)}
          onDone={() => { setReversing(null); void rows.reload() }}
          onError={setError}
        />
      )}
    </div>
  )
}

function Row({ row, canReverse, onReverse }: {
  row: Expense
  canReverse: boolean
  onReverse: () => void
}) {
  const isReversal = row.entryTypeCode === 'REVERSAL'
  const wasReversed = row.reversedById != null

  return (
    <tr className={isReversal ? 'bg-muted/40' : undefined}>
      <Td>
        <span className={wasReversed ? 'line-through text-muted-foreground' : 'font-medium'}>
          {row.reference}
        </span>
        {isReversal && row.reversesReference ? (
          <div className="text-erp-xs text-muted-foreground">
            reverses {row.reversesReference}
            {row.reversalReasonCode ? ` · ${row.reversalReasonCode.toLowerCase()
              .replace(/_/g, ' ')}` : ''}
          </div>
        ) : null}
        {wasReversed ? (
          <div className="text-erp-xs text-muted-foreground">corrected — see the later line</div>
        ) : null}
      </Td>
      <Td className="tabular-nums">{row.incurredOn}</Td>
      <Td>
        {row.categoryLabel}
        {row.purpose ? (
          <div className="text-erp-xs text-muted-foreground">{row.purpose}</div>
        ) : null}
      </Td>
      <Td>{row.payee ?? <span className="text-muted-foreground">—</span>}</Td>
      <Td>
        {row.unitName ?? <span className="text-muted-foreground">not tied to a place</span>}
        {row.eventTitle ? (
          <div className="text-erp-xs text-muted-foreground">{row.eventTitle}</div>
        ) : null}
      </Td>
      <Td className={'text-right tabular-nums ' + (isReversal ? 'text-muted-foreground' : '')}>
        {row.currency} {fmtNumber(Math.round(row.amount))}
        {row.quantity != null && row.unitAmount != null ? (
          <div className="text-erp-xs text-muted-foreground">
            {fmtNumber(row.quantity)} x {fmtNumber(Math.round(row.unitAmount))}
            {row.unitLabel ? ` · ${row.unitLabel}` : ''}
          </div>
        ) : null}
      </Td>
      <Td>
        {row.paymentMethodCode.toLowerCase().replace(/_/g, ' ')}
        {row.paymentReference ? (
          <div className="text-erp-xs text-muted-foreground tabular-nums">
            {row.paymentReference}
          </div>
        ) : null}
      </Td>
      <Td>
        {isReversal ? (
          <span className="text-muted-foreground">—</span>
        ) : row.receiptCount === 0 ? (
          <span className="text-destructive">none</span>
        ) : row.receiptAmountDisputed === true ? (
          <span className="text-amber-600 dark:text-amber-500"
                title="The receipt was read and its amount differs from the one typed">
            {row.receiptCount} · disputed
          </span>
        ) : (
          <span>{row.receiptCount}</span>
        )}
      </Td>
      <Td>
        {canReverse && !isReversal && !wasReversed ? (
          <button type="button" onClick={onReverse}
                  className="text-erp-xs underline underline-offset-2 text-muted-foreground
                             hover:text-foreground">
            Correct
          </button>
        ) : null}
      </Td>
    </tr>
  )
}

// ── dialogs ────────────────────────────────────────────────────────────────

function RecordDialog({ onClose, onDone, onError }: {
  onClose: () => void
  onDone: () => void
  onError: (message: string | null) => void
}) {
  const categories = useLoad(expenseCategories, [])
  const methods = useLoad(paymentMethods, [])
  const events = useLoad(() => listEvents({ size: 100 }), [])
  const [busy, setBusy] = useState(false)
  const [form, setForm] = useState({
    incurredOn: new Date().toISOString().slice(0, 10),
    categoryCode: '',
    purpose: '',
    payee: '',
    amount: '',
    quantity: '',
    unitAmount: '',
    unitLabel: '',
    currency: 'KES',
    paymentMethodCode: '',
    paymentReference: '',
    eventId: '',
    description: '',
  })

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement
    | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }))

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    onError(null)
    try {
      await recordExpense({
        incurredOn: form.incurredOn,
        categoryCode: form.categoryCode,
        purpose: form.purpose || null,
        payee: form.payee || null,
        // Any two of the three are enough; the server fills the third, and refuses all
        // three when they disagree rather than quietly preferring one.
        amount: form.amount ? Number(form.amount) : null,
        quantity: form.quantity ? Number(form.quantity) : null,
        unitAmount: form.unitAmount ? Number(form.unitAmount) : null,
        unitLabel: form.unitLabel || null,
        currency: form.currency,
        paymentMethodCode: form.paymentMethodCode,
        paymentReference: form.paymentReference || null,
        eventId: form.eventId || null,
        description: form.description || null,
      })
      onDone()
    } catch (err) {
      onError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog title="Record spending" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="When">
            <input type="date" required value={form.incurredOn} onChange={set('incurredOn')}
                   className="erp-input" />
          </Field>
          <Field label="What it was for">
            <select required value={form.categoryCode} onChange={set('categoryCode')}
                    className="erp-input">
              <option value="">Choose a heading…</option>
              {(categories.data ?? []).map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Total"
                 hint="Leave blank if you fill in how many and what one cost — it is worked
                       out for you.">
            <input type="number" step="0.01" min="0.01" value={form.amount}
                   onChange={set('amount')} className="erp-input" placeholder="0.00" />
          </Field>
          <Field label="Currency">
            <input value={form.currency} onChange={set('currency')} maxLength={3}
                   className="erp-input uppercase" />
          </Field>
          <Field label="How it was paid">
            <select required value={form.paymentMethodCode} onChange={set('paymentMethodCode')}
                    className="erp-input">
              <option value="">Choose…</option>
              {(methods.data ?? []).map((m) => (
                <option key={m.code} value={m.code}>{m.label}</option>
              ))}
            </select>
          </Field>
          <Field label="Reference"
                 hint="The M-Pesa code, cheque number or bank reference. This is what a
                       challenge to the return is answered with.">
            <input value={form.paymentReference} onChange={set('paymentReference')}
                   className="erp-input" placeholder="e.g. QK12AB34CD" />
          </Field>
          <Field label="Who it went to">
            <input value={form.payee} onChange={set('payee')} className="erp-input"
                   placeholder="Supplier or person paid" />
          </Field>
          <Field label="For which rally or meeting" hint="Optional. Leave blank for
                 office rent, fees and anything not tied to one event.">
            <select value={form.eventId} onChange={set('eventId')} className="erp-input">
              <option value="">Not tied to an event</option>
              {(events.data?.content ?? []).map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.title}</option>
              ))}
            </select>
          </Field>
        </div>
        {/* The three that answer "a t-shirt was 500 in August and is 750 now" — the
            question no total can. Optional, because most spend has no quantity. */}
        <fieldset className="rounded border border-border p-3">
          <legend className="px-1 text-erp-xs font-medium">
            If you bought a number of something
          </legend>
          <p className="mb-2 text-erp-xs text-muted-foreground">
            Fill these in and the platform can tell you later that a t-shirt cost 500 in
            August and 750 now — which the total alone can never show. Items are matched by
            name, so keep the spelling the same.
          </p>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="What">
              <input value={form.unitLabel} onChange={set('unitLabel')} className="erp-input"
                     placeholder="e.g. t-shirt" />
            </Field>
            <Field label="How many">
              <input type="number" step="0.001" min="0.001" value={form.quantity}
                     onChange={set('quantity')} className="erp-input" placeholder="0" />
            </Field>
            <Field label="Price of one">
              <input type="number" step="0.01" min="0.01" value={form.unitAmount}
                     onChange={set('unitAmount')} className="erp-input" placeholder="0.00" />
            </Field>
          </div>
        </fieldset>

        <Field label="Detail" hint="A short line under the heading — 'A3 posters, 2,000 copies'.">
          <input value={form.purpose} onChange={set('purpose')} className="erp-input" />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="erp-btn-secondary h-9 text-erp-sm">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="erp-btn-primary h-9 text-erp-sm">
            {busy ? 'Recording…' : 'Record it'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

function ReverseDialog({ expense, onClose, onDone, onError }: {
  expense: Expense
  onClose: () => void
  onDone: () => void
  onError: (message: string | null) => void
}) {
  const reasons = useLoad(reversalReasons, [])
  const [busy, setBusy] = useState(false)
  const [reasonCode, setReasonCode] = useState('')
  const [note, setNote] = useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    onError(null)
    try {
      await reverseExpense(expense.id, { reasonCode, note: note || null })
      onDone()
    } catch (err) {
      onError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog title={`Correct ${expense.reference}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <p className="text-erp-sm text-muted-foreground">
          This does not change or remove the entry. It files a second line for{' '}
          <b>−{expense.currency} {fmtNumber(Math.round(expense.amount))}</b> that names this
          one and says why, so the total moves and the history stays readable.
        </p>
        <Field label="Why">
          <select required value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}
                  className="erp-input">
            <option value="">Choose a reason…</option>
            {(reasons.data ?? []).map((r) => (
              <option key={r.code} value={r.code}>{r.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Note" hint="What actually happened. 'It was wrong' is not an audit trail.">
          <input value={note} onChange={(e) => setNote(e.target.value)} className="erp-input" />
        </Field>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="erp-btn-secondary h-9 text-erp-sm">
            Cancel
          </button>
          <button type="submit" disabled={busy} className="erp-btn-primary h-9 text-erp-sm">
            {busy ? 'Filing…' : 'File the correction'}
          </button>
        </div>
      </form>
    </Dialog>
  )
}

function Dialog({ title, onClose, children }: {
  title: string
  onClose: () => void
  children: React.ReactNode
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto
                    bg-black/40 p-6" role="dialog" aria-modal="true" aria-label={title}>
      <div className="w-full max-w-2xl rounded border border-border bg-card shadow-lg">
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <h2 className="text-erp-base font-medium">{title}</h2>
          <button type="button" onClick={onClose}
                  className="text-erp-sm text-muted-foreground hover:text-foreground">
            Close
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  )
}

function Field({ label, hint, children }: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <span className="block text-erp-xs font-medium">{label}</span>
      {hint ? <span className="block text-erp-xs text-muted-foreground">{hint}</span> : null}
      <span className="mt-1 block">{children}</span>
    </label>
  )
}
